import { UserRole } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useEffect, useState } from 'react';

interface MultiSelectUserRolesProps {
  size?: 'sm' | 'md';
  selectedRoles: UserRole[];
  onChange: (roles: UserRole[]) => void;
}

const validUserRoles: UserRole[] = [UserRole.ADMINISTRATOR, UserRole.MANAGER, UserRole.MAP_VIEW, UserRole.OPERATION, UserRole.USER_INSTALLER, UserRole.SAFETY_COACHER];

const MultiSelectUserRoles = ({ selectedRoles, onChange, size = 'md' }: MultiSelectUserRolesProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedRoles || []);

  const getUserRoleLabel = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMINISTRATOR:
        return t('user_roles.administrator');
      case UserRole.MANAGER:
        return t('user_roles.fleet_manager');
      case UserRole.MAP_VIEW:
        return t('user_roles.map_view');
      case UserRole.OPERATION:
        return t('user_roles.operation');
      case UserRole.USER_INSTALLER:
        return t('user_roles.installer');
      case UserRole.SAFETY_COACHER:
        return t('user_roles.safety_coacher');
    }
  };

  const hasNotChanged = selectedRoles?.length === draft?.length && selectedRoles.every((value) => draft?.includes(value));

  const onClick = useCallback(() => {
    setDraft(selectedRoles || []);
  }, [selectedRoles]);

  const handleClear = useCallback(() => {
    setDraft([]);
  }, []);

  const handleClose = useCallback(() => {
    setForceClosed(true);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const toggleItem = useCallback(
    (roleItem: UserRole) => {
      const existItem = draft.includes(roleItem);
      const newDraft = existItem ? draft.filter((value) => value !== roleItem) : [...draft, roleItem];
      setDraft(newDraft);
    },
    [draft, setDraft]
  );

  return (
    <Dropdown
      portal={false}
      onOpenChange={onClick}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='outlined' size='sm' disabled={draft?.length === 0} onClick={handleClear}>
              {t('user_roles.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightItem={30}
              data={validUserRoles}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item: UserRole) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item} key={item} checked={draft.includes(item)} onCheckedChange={() => toggleItem(item)} />
                  <Label htmlFor={item} className='font-normal'>
                    {getUserRoleLabel(item)}
                  </Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button variant='ghost' full size='sm' onClick={handleClose}>
                {t('user_roles.cancel')}
              </Button>
              <Button size='sm' full onClick={handleApply} disabled={hasNotChanged}>
                {t('user_roles.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='cog' />}>
        {t('user_roles.title')} {(selectedRoles?.length || 0) > 0 ? `(${selectedRoles?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectUserRoles;
